import { Component, HostListener } from '@angular/core';
import { ModulesService } from '../../services/modules.service';
import { User, UserTier } from '../../models/User';
import { Module } from '../../models/Module';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { Level, ScoreProgress } from '../../models/ScoresResponse';
import { QuestionsService } from '../../services/questions.service';
import { Role } from '../../models/Role';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ModalService } from '../../services/modal.service';
import { PaymentModalComponent } from '../modales/payment-modal/payment-modal.component';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { AnimationItem } from 'lottie-web';
import { ScoreService } from '../../services/score.service';

@Component({
  selector: 'app-module-end',
  standalone: true,
  imports: [CommonModule, TranslateModule, LottieComponent],
  templateUrl: './module-end.component.html',
  styleUrl: './module-end.component.scss',
})
export class ModuleEndComponent {
  user?: User;
  module?: Module;
  moduleScore?: ScoreProgress;
  moduleId: string;
  levelId: string;
  questionCount: number = 0;
  isTp: boolean = false;
  isChildModule: boolean = true;
  loaded: boolean = false;
  color?: string;
  animItem!: AnimationItem;
  medalOptions: AnimationOptions = {
    path: `/assets/lottie/medals/diamant.json`,
    autoplay: false,
    loop: true,
  };
  styles: Partial<CSSStyleDeclaration> = {
    maxWidth: '40%',
    margin: '0 auto',
  };
  nextLevel: Level | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private moduleService: ModulesService,
    private questionService: QuestionsService,
    private modalService: ModalService,
    private scoreService: ScoreService,
  ) {}

  async ngOnInit() {
    this.isTp = this.router.url.includes('_tp');
    this.user = await firstValueFrom(this.authService.getUserInfoOrRefreshIfNeeded());
    this.updateStyles();
    this.route.params.subscribe((params: Params) => {
      this.moduleId = params['id'];
      this.levelId = params['level'];
      this.color = params['color'];
      this.updateInfos();
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.updateStyles();
  }

  private updateStyles() {
    const screenWidth = window.innerWidth;
    this.styles = {
      maxWidth: screenWidth <= 720 ? '90%' : '40%',
      margin: '0 auto',
    };
  }

  private getRole(): Role {
    return this.isTp ? Role.professeur : Role.enfant;
  }

  private async updateInfos() {
    let scores = await firstValueFrom(this.authService.getMyScores(this.getRole()));
    let modules = await firstValueFrom(this.moduleService.getAllModules());
    let questions = await firstValueFrom(this.questionService.getQuestionsForModule(this.moduleId));
    const modulesChild = await firstValueFrom(this.moduleService.getModules(Role.enfant));
    const mcIds = modulesChild.map((elt) => elt.module_enum);

    this.moduleScore = scores.data[0].progresses.find((elt) => elt.module_id === this.moduleId);
    this.module = modules.find((elt) => elt.module_enum === this.moduleId);
    const lastValidatedLevel: Level = this.getLastValidated();
    this.questionCount = questions.data.filter((elt) => elt.level_id === lastValidatedLevel).length;
    this.isChildModule = mcIds.includes(this.module!.module_enum);
    this.nextLevel = this.moduleScore ? this.scoreService.getNextLevel(this.moduleScore.validated_leagues) : null;

    this.medalOptions = {
      path: `/assets/lottie/medals/${lastValidatedLevel}.json`,
      autoplay: true,
      loop: true,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    };

    const shouldAllow = await this.moduleService.canSelectModule(this.user?.tier ?? UserTier.free, lastValidatedLevel, this.moduleId);

    if (shouldAllow) {
      this.loaded = true;
    } else {
      this.openPaymentModal();
    }
  }

  getLastValidated(): Level {
    if (this.moduleScore === undefined) {
      return Level.bois;
    }

    if (this.moduleScore.validated_leagues.includes(Level.diamant)) {
      return Level.diamant;
    } else if (this.moduleScore.validated_leagues.includes(Level.or)) {
      return Level.or;
    } else if (this.moduleScore.validated_leagues.includes(Level.argent)) {
      return Level.argent;
    } else if (this.moduleScore.validated_leagues.includes(Level.bronze)) {
      return Level.bronze;
    }

    return Level.bois;
  }

  navigate() {
    if (this.user === undefined) {
      this.router.navigate(['']);
    }

    if (this.isChildModule) {
      this.router.navigate(['homec']);
    } else {
      this.router.navigate(['parcourstp']);
    }
  }

  hasNextLeague(): boolean {
    return this.nextLevel !== null;
  }

  goToNextLeague() {
    if (!this.nextLevel) {
      return;
    }

    if (this.color) {
      this.router.navigate(['module_take_tp', this.color, this.moduleId]);
    } else {
      this.router.navigate(['module_take', this.moduleId]);
    }
  }

  openPaymentModal() {
    const modalRef = this.modalService.open(PaymentModalComponent, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.result.then((result) => {
      if (result === 'cancel') {
        this.router.navigate(['']);
      }
    });
  }

  getTextColor(): any {
    let obj: any = {
      color: `${this.moduleService.getModuleColorName(this.moduleId, this.color)}`,
    };

    return obj;
  }

  animCreated(animationItem: AnimationItem): void {
    this.animItem = animationItem;
    this.animItem.play();
  }
}
